import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

const READING_ENGAGEMENT_DELAY_MS = 3000
const READING_ENGAGEMENT_SCROLL_OFFSET_PX = 24

// Hub Académie : ouvre la modale lecteur avec placeholder puis charge le turbo-frame.
export default class extends Controller {
  static values = {
    frameId: String,
    modalId: String
  }

  connect() {
    this.hubUrl = window.location.href
    this.boundOnFrameLoad = this.onFrameLoad.bind(this)
    this.boundOnModalHidden = this.onModalHidden.bind(this)
    this.boundOnAdvanceClick = this.markAsReadAndAdvance.bind(this)
    this.boundOnEngagementReset = this.onEngagementReset.bind(this)
    document.addEventListener("turbo:frame-load", this.boundOnFrameLoad)
    document.addEventListener("academy-article-reader:engagement-reset", this.boundOnEngagementReset)
    // `modal_controller` déplace la modale dans `.modals` en haut du body : son contenu
    // sort de la portée de ce contrôleur, d'où la délégation au niveau document.
    document.addEventListener("click", this.boundOnAdvanceClick)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundOnFrameLoad)
    document.removeEventListener("click", this.boundOnAdvanceClick)
    document.removeEventListener("academy-article-reader:engagement-reset", this.boundOnEngagementReset)
    this.modalElement()?.removeEventListener("hidden.bs.modal", this.boundOnModalHidden)
    this.teardownReadingEngagement()
  }

  prepareOpen(event) {
    event.preventDefault()

    const link = event.currentTarget
    const { academyArticleReaderTitle, academyArticleReaderVideo } = link.dataset

    const frame = this.frameElement()
    const href = link.getAttribute("href")
    if (frame && href) {
      // Le payload IndexOptions reste en dataset : il n'est posté qu'avec les actions de
      // lecture (POST), jamais dans le src du frame.
      if (link.dataset.academyArticleReaderIndexCardOptions) {
        frame.dataset.academyArticleReaderIndexCardOptions = link.dataset.academyArticleReaderIndexCardOptions
      } else {
        delete frame.dataset.academyArticleReaderIndexCardOptions
      }

      frame.src = href
    }

    this.applyHeader({
      title: academyArticleReaderTitle || link.textContent?.trim(),
      metaHtml: this.metaHtmlFromLink(link)
    })

    this.fillBodyPlaceholder({ hasVideo: academyArticleReaderVideo === "true" })
    this.openModal()
  }

  // Parcours « Premiers pas » : marque l'étape comme lue puis enchaîne sur la suivante.
  markAsReadAndAdvance(event) {
    const button = event.target.closest?.("[data-academy-article-reader-advance]")
    if (!button || button.disabled) return

    event.preventDefault()

    const {
      academyArticleReaderMarkUrl: markUrl,
      academyArticleReaderNextUrl: nextUrl,
      academyArticleReaderNextTitle: nextTitle,
      academyArticleReaderNextVideo: nextVideo
    } = button.dataset

    button.disabled = true

    this.markAsRead(markUrl)
      .then(() => {
        if (nextUrl) {
          this.openArticle({ url: nextUrl, title: nextTitle, hasVideo: nextVideo === "true" })
        } else {
          this.completePath()
        }
      })
      .catch(() => {
        button.disabled = false
      })
  }

  markAsRead(url) {
    if (!url) return Promise.resolve()

    return fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ compact: true, onboarding: true }),
      credentials: "same-origin"
    }).then((response) => {
      if (!response.ok) throw new Error("request failed")
      return response.text()
    }).then((html) => {
      if (html.includes("turbo-stream")) {
        window.Turbo.renderStreamMessage(html)
      }
    })
  }

  openArticle({ url, title, hasVideo }) {
    const frame = this.frameElement()
    if (frame) frame.src = url

    const link = document.querySelector(`a[href="${url}"]`)
    this.clearHeader()
    this.applyHeader({
      title: title || link?.textContent?.trim(),
      metaHtml: link ? this.metaHtmlFromLink(link) : ""
    })

    this.fillBodyPlaceholder({ hasVideo })
    this.openModal()
  }

  closeModal() {
    const modal = this.modalElement()
    if (!modal) return

    Modal.getOrCreateInstance(modal).hide()
  }

  // Dernière étape lue : la modale décolle, puis la carte de félicitations prend le relais.
  completePath() {
    const dialog = this.modalElement()?.querySelector(".modal-dialog")
    dialog?.classList.add("academy-article-reader__dialog--completing")

    window.setTimeout(() => {
      this.closeModal()
      dialog?.classList.remove("academy-article-reader__dialog--completing")
      document.dispatchEvent(new CustomEvent("academy-onboarding:completed"))
    }, 460)
  }

  metaHtmlFromLink(link) {
    const fromData = this.metaHtmlFromLinkDataset(link)
    if (fromData) return fromData

    return this.metaHtmlFromCard(link)
  }

  metaHtmlFromLinkDataset(link) {
    const encoded = link.dataset.academyArticleReaderMeta
    if (!encoded) return ""

    try {
      return this.decodeBase64Utf8(encoded).trim()
    } catch (_error) {
      return ""
    }
  }

  decodeBase64Utf8(encoded) {
    const binary = atob(encoded)
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  }

  metaHtmlFromCard(link) {
    const root = link.closest(".academy-article-card, .academy-onboarding-path__item, .academy-onboarding-placement__preview, tr.index-scroll-table__row")
    const source = root?.querySelector("[data-academy-article-reader-card-meta]")
    const fromSource = source?.innerHTML?.trim()
    if (fromSource) return fromSource

    if (root?.classList.contains("academy-article-card")) {
      return this.metaHtmlFromIndexCard(root)
    }

    if (root?.matches("tr.index-scroll-table__row")) {
      return this.metaHtmlFromTableRow(root)
    }

    return ""
  }

  metaHtmlFromIndexCard(card) {
    const parts = [
      card.querySelector(".academy-article-card__reading-time")?.innerHTML?.trim(),
      card.querySelector(".academy-article-card__statuses")?.innerHTML?.trim(),
      card.querySelector(".academy-article-card__tags")?.innerHTML?.trim()
    ].filter(Boolean)

    if (!parts.length) return ""

    return `<div class="academy-article-reader__header-meta d-flex flex-wrap align-items-center gap-2">${parts.join("")}</div>`
  }

  metaHtmlFromTableRow(row) {
    const readingTimeCell = row.querySelector(".index-scroll-table__column--reading-time .index-scroll-table__cell-content")
    const readingTime = readingTimeCell?.textContent?.trim()
    const readingTimeHtml = readingTime
      ? `<span class="academy-article-reader__header-reading-time text-muted small">${readingTime}</span>`
      : ""

    const progressHtml = row.querySelector(".index-scroll-table__column--reading-progress .index-scroll-table__cell-content")?.innerHTML?.trim()
    const subproduct = row.querySelector(".index-scroll-table__column--subproduct .index-scroll-table__cell-content")?.textContent?.trim()
    const subproductHtml = subproduct ? `<span class="academy-tag-chip">${subproduct}</span>` : ""

    const parts = [readingTimeHtml, progressHtml, subproductHtml].filter(Boolean)
    if (!parts.length) return ""

    return `<div class="academy-article-reader__header-meta d-flex flex-wrap align-items-center gap-2">${parts.join("")}</div>`
  }

  modalElement() {
    return document.getElementById(this.modalIdValue)
  }

  frameElement() {
    return document.getElementById(this.frameIdValue)
  }

  modalTitleElement() {
    return (
      document.getElementById(`${this.modalIdValue}Label`) ||
      this.modalElement()?.querySelector(".modal-title")
    )
  }

  modalMetaElement() {
    return (
      document.getElementById("academy_article_reader_modal_meta") ||
      this.modalElement()?.querySelector("[data-academy-article-reader-modal-meta]")
    )
  }

  applyHeader({ title, metaHtml }) {
    const modalTitle = this.modalTitleElement()
    const modalMeta = this.modalMetaElement()

    if (modalTitle && title) modalTitle.textContent = title
    if (modalMeta && metaHtml?.trim()) {
      modalMeta.innerHTML = metaHtml.trim()
    }
  }

  syncHeaderFromFrame(frame) {
    const sync = frame.querySelector("[data-academy-article-reader-header-sync]")
    if (!sync) return

    const title = sync.dataset.academyArticleReaderSyncTitle
    this.applyHeader({ title: title || undefined })
  }

  fillBodyPlaceholder({ hasVideo }) {
    const modal = this.modalElement()
    const frame = this.frameElement()
    const placeholder = modal?.querySelector("[data-academy-article-reader-placeholder]")
    if (!modal || !placeholder) return

    placeholder.querySelectorAll("[data-academy-article-reader-skeleton-video]").forEach((videoSkeleton) => {
      videoSkeleton.classList.toggle("d-none", !hasVideo)
      videoSkeleton.setAttribute("aria-hidden", hasVideo ? "false" : "true")
    })

    placeholder.classList.remove("d-none")
    frame?.classList.add("d-none")
  }

  hidePlaceholder() {
    const modal = this.modalElement()
    const frame = this.frameElement()
    modal?.querySelector("[data-academy-article-reader-placeholder]")?.classList.add("d-none")
    frame?.classList.remove("d-none")
  }

  onFrameLoad(event) {
    if (event.target.id !== this.frameIdValue) return

    this.syncHeaderFromFrame(event.target)
    this.hidePlaceholder()
    this.setupReadingEngagement(event.target)
    this.openModal()
  }

  openModal() {
    const modal = this.modalElement()
    if (!modal) return

    modal.removeEventListener("hidden.bs.modal", this.boundOnModalHidden)
    modal.addEventListener("hidden.bs.modal", this.boundOnModalHidden)

    const dialog = modal.querySelector(".modal-dialog")
    dialog?.classList.add("academy-article-reader__dialog--opening")
    window.setTimeout(() => dialog?.classList.remove("academy-article-reader__dialog--opening"), 220)

    Modal.getOrCreateInstance(modal).show()
  }

  onModalHidden() {
    this.teardownReadingEngagement()
    this.engagementSuppressed = false
    this.restoreHubUrl()
    this.resetFrame()
    this.clearHeader()
    const modal = this.modalElement()
    modal?.querySelector("[data-academy-article-reader-placeholder]")?.classList.add("d-none")
    modal?.querySelectorAll("[data-academy-article-reader-skeleton-video]").forEach((videoSkeleton) => {
      videoSkeleton.classList.add("d-none")
      videoSkeleton.setAttribute("aria-hidden", "true")
    })
  }

  clearHeader() {
    const modalTitle = this.modalTitleElement()
    const modalMeta = this.modalMetaElement()
    if (modalTitle) modalTitle.textContent = ""
    if (modalMeta) modalMeta.innerHTML = ""
  }

  resetFrame() {
    const frame = this.frameElement()
    if (!frame) return

    frame.removeAttribute("src")
    frame.innerHTML = ""
    frame.classList.remove("d-none")
    delete frame.dataset.academyArticleReaderIndexCardOptions
  }

  restoreHubUrl() {
    if (!this.hubUrl) return

    const current = new URL(window.location.href)
    if (current.searchParams.get("modal") !== "true") return

    window.history.replaceState(window.history.state, "", this.hubUrl)
  }

  onEngagementReset() {
    this.engagementSuppressed = true
    this.teardownReadingEngagement()
  }

  setupReadingEngagement(frame) {
    this.teardownReadingEngagement()
    this.engagementSuppressed = false

    const marker = frame.querySelector("[data-academy-article-reader-engagement]")
    if (!marker) return

    const trackUrl = marker.dataset.academyArticleReaderEngagementTrackUrl
    if (!trackUrl) return

    this.readingEngagement = {
      tracked: false,
      trackUrl,
      indexCardOptions: frame.dataset.academyArticleReaderIndexCardOptions || ""
    }

    this.boundEngagementScroll = this.onReadingEngagementScroll.bind(this)
    this.readingEngagementScrollRoots = this.readingEngagementScrollRootsFor(frame)
    this.readingEngagementScrollRoots.forEach((root) => {
      root.addEventListener("scroll", this.boundEngagementScroll, { passive: true })
    })

    this.readingEngagementTimer = window.setTimeout(
      () => this.trackReadingEngagement("time"),
      READING_ENGAGEMENT_DELAY_MS
    )
  }

  readingEngagementScrollRootsFor(frame) {
    const roots = []
    const content = frame.querySelector(".academy-article-reader__content")
    if (content) roots.push(content)

    const modal = this.modalElement()
    const modalBody = frame.closest(".modal-body") || modal?.querySelector(".modal-body")
    if (modalBody) roots.push(modalBody)

    const modalDialog = modal?.querySelector(".modal-dialog")
    if (modalDialog) roots.push(modalDialog)

    return [...new Set(roots)]
  }

  onReadingEngagementScroll(event) {
    const root = event.currentTarget
    if (root.scrollTop >= READING_ENGAGEMENT_SCROLL_OFFSET_PX) {
      this.trackReadingEngagement("scroll")
    }
  }

  trackReadingEngagement(_reason) {
    if (this.engagementSuppressed || !this.readingEngagement || this.readingEngagement.tracked) return

    this.readingEngagement.tracked = true
    this.teardownReadingEngagementListeners()

    const body = { compact: true }
    if (this.readingEngagement.indexCardOptions) {
      body.index_card_options = this.readingEngagement.indexCardOptions
    }

    fetch(this.readingEngagement.trackUrl, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(body),
      credentials: "same-origin"
    })
      .then((response) => {
        if (!response.ok) throw new Error("request failed")
        return response.text()
      })
      .then((html) => {
        if (html.includes("turbo-stream")) {
          window.Turbo.renderStreamMessage(html)
        }
      })
      .catch(() => {})
  }

  teardownReadingEngagementListeners() {
    if (this.readingEngagementTimer) {
      window.clearTimeout(this.readingEngagementTimer)
      this.readingEngagementTimer = null
    }

    if (this.boundEngagementScroll && this.readingEngagementScrollRoots) {
      this.readingEngagementScrollRoots.forEach((root) => {
        root.removeEventListener("scroll", this.boundEngagementScroll)
      })
    }

    this.readingEngagementScrollRoots = null
    this.boundEngagementScroll = null
  }

  teardownReadingEngagement() {
    this.teardownReadingEngagementListeners()
    this.readingEngagement = null
  }
}
